import { apiRoutes } from '@socialfy/api-contract';
import { createContext, useContext } from 'react';
import type { ResponseData } from './types';

export type SessionMe = ResponseData<typeof apiRoutes.core.getMe>;
export type SessionMode = SessionMe['mode'];

/**
 * `idle`: Benutzer wird gerade nicht geladen (Seite „Kein Zugang“); `loading`; `ready`; `no-access`: angemeldet,
 * aber nicht freigeschaltet; `error`: der Benutzer konnte nicht geladen werden.
 */
export type SessionStatus = 'idle' | 'loading' | 'ready' | 'no-access' | 'error';

export type SessionValue = {
  status: SessionStatus;
  me: SessionMe | undefined;
  error: Error | null;
  /** Wechselt zwischen Beispieldaten und eigenen Daten; danach werden alle Abfragen neu geladen. */
  setMode(mode: SessionMode): Promise<void>;
  /** Meldet ab und leitet zur Anmeldung. */
  logout(): Promise<void>;
  /** Lädt den Benutzer neu. */
  refresh(): Promise<void>;
};

export const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const session = useContext(SessionContext);
  if (session === null) {
    throw new Error('useSession muss innerhalb von SessionProvider verwendet werden');
  }
  return session;
}

export const LOGIN_PATH = '/api/v1/auth/login';

/**
 * Adresse der Anmeldung mit Rücksprungziel auf die aktuelle Seite. Das Ziel wird gegen dasselbe Schema
 * geprüft, das auch der Server für `returnTo` verwendet (`ReturnTo` der Route `core.login`, V12-05); besteht
 * es nicht (`//evil.example`, `/\evil`, Steuerzeichen), gibt es kein `returnTo`.
 */
export function loginUrl(location: { pathname: string; search: string }): string {
  const target = `${location.pathname}${location.search}`;
  const valid = apiRoutes.core.login.route.request.query.safeParse({ returnTo: target }).success;
  return valid ? `${LOGIN_PATH}?returnTo=${encodeURIComponent(target)}` : LOGIN_PATH;
}
